import React from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useSiteReimportStore } from '../../../stores/siteReimportStore';
import type { PlotDiff, ChangeType } from '../../../types/reimportTypes';
import ReimportLabourItemRow from './ReimportLabourItemRow';

interface ReimportPlotRowProps {
  plot: PlotDiff;
  showUnchangedItems?: boolean;
}

const getPlotChangeLabel = (
  plot: PlotDiff
): { label: string; color: 'success' | 'error' | 'warning' | 'default' } => {
  if (plot.changeType === 'Added')
    return { label: '+ plot added', color: 'success' };
  if (plot.changeType === 'Removed')
    return { label: '- plot removed', color: 'error' };
  if (plot.changeType === 'Unchanged')
    return { label: '= unchanged', color: 'default' };

  // Modified — describe what changed from the labour items
  const hasAdded = plot.labourItems.some(i => i.changeType === 'Added');
  const hasRemoved = plot.labourItems.some(i => i.changeType === 'Removed');
  const hasPriceChange = plot.labourItems.some(
    i => i.changeType === 'Modified'
  );

  const parts: string[] = [];
  if (hasAdded) parts.push('items added');
  if (hasRemoved) parts.push('items removed');
  if (hasPriceChange) parts.push('prices changed');

  const label = parts.length > 0 ? `~ ${parts.join(', ')}` : '~ modified';
  return { label, color: 'warning' };
};

const bgTint: Record<ChangeType, string | undefined> = {
  Added: 'success.50',
  Removed: 'error.50',
  Modified: 'warning.50',
  Unchanged: undefined,
};

const ReimportPlotRow: React.FC<ReimportPlotRowProps> = ({
  plot,
  showUnchangedItems = false,
}) => {
  const { expandedRows, toggleRowExpansion } = useSiteReimportStore();

  const isExpanded = expandedRows.has(plot.plotNumber);
  const config = getPlotChangeLabel(plot);
  const totalCost = plot.labourItems.reduce((sum, item) => {
    if (item.changeType === 'Removed') return sum;
    const price = item.newPrice ?? item.currentPrice ?? 0;
    return sum + price;
  }, 0);

  const visibleItems = showUnchangedItems
    ? plot.labourItems
    : plot.labourItems.filter(item => item.changeType !== 'Unchanged');
  const hiddenCount = plot.labourItems.length - visibleItems.length;

  return (
    <>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          bgcolor: bgTint[plot.changeType],
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => toggleRowExpansion(plot.plotNumber)}
          >
            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Chip label={config.label} color={config.color} size="small" />
        </TableCell>
        <TableCell component="th" scope="row">
          {plot.plotNumber}
        </TableCell>
        <TableCell>{plot.plotName}</TableCell>
        <TableCell align="center">
          <Chip
            label={`${plot.labourItems.length} item${plot.labourItems.length !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight="medium">
            £{totalCost.toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {plot.hasProtectedItems && (
            <LockIcon fontSize="small" color="action" />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ pb: 0, pt: 0 }} colSpan={7}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="subtitle2" gutterBottom component="div">
                Labour Items ({plot.labourItems.length})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Labour Item</TableCell>
                    <TableCell>Change</TableCell>
                    <TableCell align="right">Current</TableCell>
                    <TableCell align="right">New</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleItems.map((item, index) => (
                    <ReimportLabourItemRow
                      key={`${item.definitionName}-${index}`}
                      item={item}
                    />
                  ))}
                  {hiddenCount > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        sx={{ py: 1, color: 'text.secondary' }}
                      >
                        <Typography variant="caption">
                          {hiddenCount} unchanged item
                          {hiddenCount !== 1 ? 's' : ''} hidden
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ReimportPlotRow;
